// The card of a person of the team in the stack of modals (R-A3, R-A4, R-U27): their roles limited to events and in time, the login link,
// removal from the team. Roles are a working copy saved by «Сохранить»; leaving the card with changes asks the core's question (B2).

import { useMemo, useState } from 'react';
import { grantLive, recordTitle, type Grant, type UserDef } from '@confdeck/core-shared';
import { PickList } from '../ui/PickList.tsx';
import { useMeta, useSync, useTypesTick } from '../sync/react.tsx';
import { useUnsaved } from '../shell/unsaved.ts';
import { StackPane } from '../ui/ModalStack.tsx';
import { PageBlock } from '../ui/Page.tsx';
import { Button, Checkbox, ConfirmDialog, Field, IconButton, Menu, MenuContent, MenuItem, MenuTrigger, Popover, PopoverContent, PopoverTrigger, useToast } from '../ui/primitives.tsx';
import { DateInput } from '../ui/DateInput.tsx';
import s from './team.module.css';

/** The server may write a grant with its empty keys spelled out: two lists are the same when they give the same roles. */
const canon = (gs: Grant[]) => JSON.stringify(gs.map((g) => [g.roleId, g.scopeIds?.length ? g.scopeIds : null, g.until || null, !!g.disabled]));
export const sameGrants = (a: Grant[], b: Grant[]) => canon(a) === canon(b);

export function PersonCard({ user, layer, onOpenRole, onClose }: {
  user: UserDef;
  /** The key of the card's layer: closing it asks only about this card's roles. */
  layer: string;
  onOpenRole: (roleId: string) => void;
  onClose: () => void;
}) {
  const meta = useMeta();
  const client = useSync();
  const toast = useToast();
  const [grants, setGrants] = useState<Grant[]>(user.grants ?? []);
  const roles = meta.roles().filter((r) => !r.archivedAt);
  // An external person gets roles with their sections only (R-A9): full access is not offered.
  const choosable = user.external ? roles.filter((r) => !r.full) : roles;
  const scope = client.header?.scope;
  const eventsTick = useTypesTick(scope ? [scope.typeId] : []);
  const events = useMemo(() => (scope ? [...client.store.recordIds(scope.typeId)].map((id) => client.store.record(id)!).filter(Boolean).map((r) => ({ id: r.id, label: recordTitle(r, meta) })) : []), [scope, client.store, meta, eventsTick]);
  // «Добавить роль» offers a role the person does not have yet (P3-2); with every role given there is nothing to add.
  const nextRole = choosable.find((r) => !grants.some((g) => g.roleId === r.id));
  const dirty = user.role !== 'owner' && sameGrants(grants, user.grants ?? []) === false;
  const save = async (): Promise<boolean> => {
    const r = await client.run('user.setGrants', { id: user.id, grants });
    toast(r.ok ? { kind: 'info', text: 'Роли сохранены' } : { kind: 'error', text: r.error.message });
    return r.ok;
  };
  useUnsaved(dirty, save, `роли: ${user.name}`, layer);
  // The owner never sets someone's password (П8): the person gets a link and sets it themselves. A reset ends the password that
  // works, so it is asked in the core's dialog (DESIGN.md «Настройки»: the irreversible asks); a repeated invitation does not.
  const [asking, setAsking] = useState<'reset' | 'remove' | null>(null);
  const [busy, setBusy] = useState(false);
  const sendLink = async () => {
    setBusy(true);
    const r = await client.run('user.resendInvite', { id: user.id });
    setBusy(false); setAsking(null);
    toast(r.ok ? { kind: 'info', text: 'Письмо со ссылкой отправлено' } : { kind: 'error', text: r.error.message });
  };
  const remove = async () => {
    setBusy(true);
    const r = await client.run('user.archive', { id: user.id });
    setBusy(false); setAsking(null);
    toast(r.ok ? { kind: 'info', text: `${user.name}: доступ закрыт` } : { kind: 'error', text: r.error.message });
    if (r.ok) onClose();
  };
  const sub = <>{user.email}{user.role === 'owner' ? ' · владелец аккаунта' : user.external ? ' · внештатный' : ''}</>;
  if (user.role === 'owner') {
    return <StackPane title={user.name} sub={sub} onClose={onClose}><p className={s.help}>Владелец аккаунта: все права всегда, роли не нужны (R-A2). Имя и фото он меняет сам в профиле.</p></StackPane>;
  }
  const patch = (i: number, p: Partial<Grant>) => setGrants(grants.map((x, j) => (j === i ? { ...x, ...p } : x)));
  return (
    <StackPane title={user.name} sub={sub} onClose={onClose} footer={(
      <>
        {nextRole && <Button size="sm" variant="ghost" icon="plus" onClick={() => setGrants([...grants, { roleId: nextRole.id, scopeIds: null }])}>Добавить роль</Button>}
        <span className={s.spacer} />
        {dirty && <Button size="sm" onClick={() => setGrants(user.grants ?? [])}>Отменить</Button>}
        <Button size="sm" variant="primary" disabled={!dirty} onClick={() => void save()}>Сохранить</Button>
      </>
    )}>
      <h3 className={s.h3First}>Роли</h3>
      {!grants.length && <p className={s.help}>{user.external ? 'Ролей нет — раздела «Работа» в личном кабинете не будет. Выдайте роль, например «Волонтёр», и ограничьте её событием.' : 'Ролей нет — человек входит, но ничего не видит. Выдайте роль.'}</p>}
      {grants.map((g, i) => (
        <div key={i} className={s.grant}>
          <Menu>
            <MenuTrigger asChild><Button size="sm" variant="secondary" caret>{meta.role(g.roleId)?.name ?? 'Роль'}</Button></MenuTrigger>
            <MenuContent>{choosable.map((r) => <MenuItem key={r.id} onSelect={() => patch(i, { roleId: r.id })}>{r.name}</MenuItem>)}</MenuContent>
          </Menu>
          <IconButton size="sm" icon="open" label="Открыть роль" onClick={() => onOpenRole(g.roleId)} />
          {scope && (
            <Popover>
              <PopoverTrigger asChild><Button size="sm" variant="ghost" caret>{g.scopeIds?.length ? g.scopeIds.map((id) => events.find((e) => e.id === id)?.label ?? '—').join(', ') : `${scope.label}: все`}</Button></PopoverTrigger>
              <PopoverContent width={320}>
                <PickList options={events} multiple selected={g.scopeIds ?? []} placeholder={`Найти: ${scope.label.toLowerCase()}`}
                  onPick={(id) => { const cur = g.scopeIds ?? []; const next = cur.includes(id) ? cur.filter((y) => y !== id) : [...cur, id]; patch(i, { scopeIds: next.length ? next : null }); }} />
              </PopoverContent>
            </Popover>
          )}
          {/* П7: the last day of the grant and «switched off» without removing it. */}
          <label className={s.until}><span>до</span><DateInput value={g.until ?? ''} aria-label="Роль действует до" onChange={(v) => patch(i, { until: v || null })} /></label>
          <Checkbox className={s.until} checked={!g.disabled} onChange={(on) => patch(i, { disabled: !on })}>{g.disabled ? 'отключена' : !grantLive(g) ? 'срок вышел' : 'действует'}</Checkbox>
          <IconButton size="sm" icon="close" label="Снять роль" onClick={() => setGrants(grants.filter((_, j) => j !== i))} />
        </div>
      ))}
      <PageBlock title="Вход" rows>
        <Field as="div" inline="end" label={user.external ? 'Через личный кабинет' : user.pending ? 'Приглашение отправлено' : 'Пароль задан'}
          hint={user.external ? 'Пароля команды нет: человек открывает рабочие разделы из своего личного кабинета, раздел «Работа».' : user.pending ? 'Пароль ещё не задан: человек задаст его по ссылке из письма.' : 'Забыл — отправьте ссылку, по ней человек задаст новый.'}>
          {user.external ? null : <Button size="sm" variant="secondary" icon="mail" disabled={busy} onClick={() => (user.pending ? void sendLink() : setAsking('reset'))}>{user.pending ? 'Отправить ещё раз' : 'Сбросить пароль'}</Button>}
        </Field>
      </PageBlock>
      <PageBlock title="Опасная зона" tone="danger" rows>
        <Field as="div" inline="end" label="Убрать из команды" hint="Человек сразу теряет доступ: открытые страницы закроются, войти он не сможет. Его записи и история остаются.">
          <Button size="sm" variant="danger" onClick={() => setAsking('remove')}>Убрать</Button>
        </Field>
      </PageBlock>
      <ConfirmDialog open={asking === 'reset'} title={`Сбросить пароль: ${user.name}?`} action="Сбросить и отправить" busy={busy} onConfirm={() => void sendLink()} onClose={() => setAsking(null)}>
        Текущий пароль перестанет работать сразу. На {user.email} уйдёт письмо со ссылкой: по ней человек задаст новый.
      </ConfirmDialog>
      <ConfirmDialog open={asking === 'remove'} title={`Убрать из команды: ${user.name}?`} action="Убрать из команды" danger busy={busy} onConfirm={() => void remove()} onClose={() => setAsking(null)}>
        Доступ закроется сразу: открытые страницы закроются, войти человек не сможет. Записи, которые он вёл, и история изменений остаются.
      </ConfirmDialog>
    </StackPane>
  );
}
